import React, { useState } from 'react';
import { ServiceKey } from '../data/serviceDetails';
import { Award, Share2, Megaphone, Globe, Search, Zap, Server } from 'lucide-react';

import brandingImg from '../assets/images/ke_us_service_branding_1793540082363.jpg';
import socialImg from '../assets/images/ke_us_service_social_1793540084351.jpg';
import adsImg from '../assets/images/ke_us_service_ads_1793540085154.jpg';
import websitesImg from '../assets/images/ke_us_service_websites_1793540085328.jpg';
import searchImg from '../assets/images/ke_us_service_geo_search_1793540086662.jpg';
import automationImg from '../assets/images/ke_us_service_automation_1793540087784.jpg';
import hostingImg from '../assets/images/ke_us_service_cloud_hosting_1793540088102.jpg';
import officeCustomerCareImg from '../assets/images/xweba_office_customer_care_bg_1793403437018.jpg';

export const XWEBA_OFFICE_CUSTOMER_CARE_IMAGE = officeCustomerCareImg;

export interface ServiceVisualMeta {
  src: string;
  alt: string;
  caption: string;
  pillarLabel: string;
  icon: React.ComponentType<{ className?: string }>;
}

export const SERVICE_IMAGE_MAP: Record<ServiceKey, ServiceVisualMeta> = {
  branding: {
    src: brandingImg,
    alt: 'Kenyan and Black American creative directors reviewing corporate identity guidelines, Pantone swatches, and logo typography in a modern design studio',
    caption: '20+ Years Hands-On Brand Identity & Visual Design Systems',
    pillarLabel: '01 · Branding Consultant',
    icon: Award,
  },
  social: {
    src: socialImg,
    alt: 'Black American and Kenyan social media strategists collaborating on multi-platform content calendars and audience engagement analytics',
    caption: 'Omnichannel Social Media Strategy & Executive Authority',
    pillarLabel: '02 · Social Media Consulting',
    icon: Share2,
  },
  ads: {
    src: adsImg,
    alt: 'Kenyan and Black American performance marketing specialists analyzing high-ROAS paid media campaigns and conversion analytics',
    caption: 'Algorithmic Paid Media & High-ROAS Smart Advertising',
    pillarLabel: '03 · Digital / Smart Ads',
    icon: Megaphone,
  },
  websites: {
    src: websitesImg,
    alt: 'Kenyan and Black American web architects engineering a high-speed Pro WordPress and custom AI-ready website with structured schema',
    caption: 'Pro WordPress & Custom Websites Engineered for AI Citation',
    pillarLabel: '04 · Pro WordPress & AI Web',
    icon: Globe,
  },
  search: {
    src: searchImg,
    alt: 'Black American and Kenyan AI search engineers analyzing Generative Engine Optimization (GEO) knowledge graphs and AI citation metrics',
    caption: 'Generative Engine Optimization (GEO) & Knowledge Graph Authority',
    pillarLabel: '05 · AI Search Visibility',
    icon: Search,
  },
  automation: {
    src: automationImg,
    alt: 'Kenyan and Black American CRM solutions architects reviewing automated lead qualification workflows and instant WhatsApp routing',
    caption: '24/7 AI Lead Qualification, CRM Sync & <60s WhatsApp Routing',
    pillarLabel: '06 · AI CRM Automation',
    icon: Zap,
  },
  hosting: {
    src: hostingImg,
    alt: 'Kenyan and Black American cloud infrastructure engineers monitoring Hostinger Cloud servers, global CDN routing, and 99.9% uptime telemetry',
    caption: 'Official Hostinger Cloud Partner Provisioning & 99.9% Uptime SLA',
    pillarLabel: '07 · Managed Cloud Hosting',
    icon: Server,
  },
};

export interface BrandedServiceImageProps {
  serviceKey?: ServiceKey;
  customSrc?: string;
  customTag?: string;
  customCaption?: string;
  title?: string;
  subtitle?: string;
  badgeText?: string;
  metricBadge?: string;
  aspectClass?: string;
  compact?: boolean;
  className?: string;
}

export const BrandedServiceImage: React.FC<BrandedServiceImageProps> = ({
  serviceKey = 'branding',
  customSrc,
  customTag,
  customCaption,
  title,
  subtitle,
  badgeText,
  metricBadge,
  aspectClass,
  compact = false,
  className = '',
}) => {
  const [imgError, setImgError] = useState(false);
  const meta = SERVICE_IMAGE_MAP[serviceKey] || SERVICE_IMAGE_MAP.branding;
  const Icon = meta.icon;

  const displaySrc = customSrc || meta.src;
  const displayPillarLabel = customTag || meta.pillarLabel;
  const displayBadge = metricBadge || badgeText;
  const displayCaption = customCaption || title || meta.caption;
  const resolvedAspect = aspectClass || (compact ? 'aspect-16/10' : 'aspect-16/9');

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-slate-200/80 bg-[#071E38] group/img ${resolvedAspect} ${className}`}
    >
      {!imgError ? (
        <img
          src={displaySrc}
          alt={title ? `${title}${subtitle ? ` - ${subtitle}` : ''}` : meta.alt}
          referrerPolicy="no-referrer"
          loading="lazy"
          onError={() => setImgError(true)}
          className="w-full h-full object-cover object-center transition-transform duration-500 group-hover/img:scale-105"
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center bg-gradient-to-br from-[#0B2D52] via-[#071E38] to-slate-900">
          <div className="w-12 h-12 rounded-2xl bg-[#F26522]/20 border border-[#F26522]/40 flex items-center justify-center text-[#F26522] mb-3">
            <Icon className="w-6 h-6" />
          </div>
          <p className="text-xs font-bold text-white">{displayPillarLabel}</p>
          <p className="text-[11px] text-slate-300 mt-1 max-w-xs">{displayCaption}</p>
        </div>
      )}

      {/* Measured contrast scrim for legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#071E38]/90 via-[#071E38]/25 to-transparent pointer-events-none" />

      {/* Top-left subtle Xweba wordmark + pillar label */}
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between gap-2 pointer-events-none">
        <span className="px-2.5 py-1 rounded-md bg-[#071E38]/85 backdrop-blur-xs border border-white/15 text-[11px] font-mono font-bold text-white truncate">
          {displayPillarLabel}
        </span>
        {displayBadge && (
          <span className="px-2.5 py-1 rounded-md bg-[#F26522] text-white text-[11px] font-bold shadow-sm whitespace-nowrap shrink-0">
            {displayBadge}
          </span>
        )}
      </div>

      {/* Bottom caption */}
      <div className="absolute bottom-3 left-3 right-3 pointer-events-none">
        <p className="text-xs font-semibold text-white/95 leading-snug line-clamp-1">
          {displayCaption}
        </p>
        {subtitle && (
          <p className="text-[10px] text-slate-300 leading-tight line-clamp-1 mt-0.5">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
};
